// Kiln Mauler (replaces `brute`) — elite: Furnace Executioner. Render-only art (ENEMY-REFORGE, P1 §2 #03, §4).
// Upper-body heavy: a stone-plated shoulder yoke leads the silhouette, a small head sits recessed between the humps,
// forearms are enormous and the fists hang near knee height. Short thick legs, heavy shoulder-led stomp.
// Wind-up: the right fist rises high overhead (the left arm braces forward).
import { ArtBuilder, ArtGeo, at, EnemyArt, limb, PAL, plate } from './kit';
import { armPhase, blob, drum, legPhase, spike } from './parts_heavy';

const STONE = 0x8a8682, STONE_DARK = 0x5f5b59, STONE_LIGHT = 0xa8a39d;
const CRACK = 0xd2601e, CRACK_HOT = 0xffa53a;

const HIP_Y = 1.15, LEG_SWING = 0.42, ARM_SWING = 0.3;

function build(elite: boolean): ArtGeo {
  const b = new ArtBuilder();
  const crack = elite ? CRACK_HOT : CRACK;

  // ---- legs: short and thick, whole leg swings about the hip
  for (const s of [-1, 1]) {
    const leg = { pivot: [s * 0.42, HIP_Y, -0.05] as [number, number, number], swing: LEG_SWING, sphase: legPhase(s) };
    b.add(limb([s * 0.42, 1.22, -0.05], [s * 0.5, 0.62, 0.08], 0.28, 0.22, 10), { color: PAL.iron, ...leg });
    b.add(at(plate(0.3, 0.42, 0.14, 0.05, 1), s * 0.53, 0.95, 0.2, -0.15, 0, s * -0.12), { color: STONE, ...leg });
    b.add(blob(0.2, s * 0.5, 0.62, 0.13, [1, 0.9, 0.85], 8, 5), { color: STONE_DARK, ...leg });
    b.add(limb([s * 0.5, 0.62, 0.06], [s * 0.5, 0.17, -0.02], 0.21, 0.18, 10), { color: PAL.iron, ...leg });
    b.add(at(plate(0.46, 0.2, 0.62, 0.07), s * 0.5, 0.1, 0.06), { color: STONE_DARK, ...leg });
    b.add(at(plate(0.42, 0.1, 0.16, 0.04, 1), s * 0.5, 0.15, 0.36), { color: PAL.iron, ...leg });
  }

  // ---- pelvis + belly
  b.add(at(plate(0.95, 0.36, 0.62, 0.08), 0, 1.22, -0.04), { color: PAL.iron });
  b.add(at(plate(0.48, 0.4, 0.08, 0.03, 1), 0, 1.04, 0.3, -0.1), { color: STONE_DARK });
  b.add(blob(0.45, 0, 1.48, 0.06, [1.2, 0.75, 0.9], 10, 6), { color: PAL.ironLight });

  // ---- chest + shoulder yoke (the silhouette leader)
  b.add(blob(0.62, 0, 1.88, 0.05, [1.25, 0.95, 0.85], 12, 8), { color: STONE });
  b.add(blob(0.56, 0, 2.22, -0.2, [1.35, 0.8, 0.95], 10, 6), { color: STONE_DARK });
  for (const s of [-1, 1]) {
    b.add(blob(0.48, s * 0.84, 2.28, 0.02, [1.05, 0.92, 1.0], 12, 7), { color: STONE });
    // layered stone pauldron slabs, tilted down the slope of the shoulder
    b.add(at(plate(0.72, 0.16, 0.8, 0.05), s * 0.9, 2.66, 0.0, 0, 0, s * -0.38), { color: STONE_LIGHT });
    b.add(at(plate(0.5, 0.14, 0.66, 0.05, 1), s * 1.18, 2.42, 0.02, 0, 0, s * -0.85), { color: STONE_DARK });
    // furnace crack across each shoulder
    b.add(at(plate(0.32, 0.04, 0.04, 0.005, 1), s * 0.86, 2.73, 0.4, 0, 0, s * -0.38), { color: crack, glow: true });
    // kiln stacks on the back
    b.add(drum(0.11, 0.14, 0.42, s * 0.34, 2.64, -0.48, -0.35, 0, s * 0.12, 8), { color: PAL.iron });
    b.add(drum(0.1, 0.1, 0.04, s * 0.355, 2.85, -0.555, -0.35, 0, s * 0.12, 8), { color: crack, glow: true });
  }
  // chest crack + furnace grate
  b.add(at(plate(0.05, 0.32, 0.05, 0.005, 1), 0.1, 1.98, 0.58, -0.2, 0, 0.45), { color: crack, glow: true });
  b.add(at(plate(0.05, 0.26, 0.05, 0.005, 1), -0.04, 1.74, 0.6, -0.1, 0, -0.35), { color: crack, glow: true });
  b.add(at(plate(0.34, 0.14, 0.05, 0.005, 1), 0, 1.5, 0.5, -0.25), { color: crack, glow: true });
  b.add(at(plate(0.42, 0.2, 0.06, 0.02, 1), 0, 1.5, 0.48, -0.25), { color: PAL.black });

  // ---- recessed head: sits low and forward between the humps
  b.add(blob(0.21, 0, 2.3, 0.46, [1, 0.95, 1.05], 10, 6), { color: PAL.iron });
  b.add(at(plate(0.46, 0.11, 0.28, 0.04, 1), 0, 2.43, 0.53, 0.15), { color: STONE_LIGHT });
  b.add(at(plate(0.3, 0.12, 0.2, 0.04, 1), 0, 2.16, 0.55), { color: STONE_DARK });
  b.add(at(plate(0.26, 0.045, 0.04, 0.005, 1), 0, 2.33, 0.67), { color: crack, glow: true });

  // ---- arms: huge stone forearms, fists near knee height
  for (const s of [-1, 1]) {
    const strike = s < 0; // the right arm (-X) lifts its fist for the wind-up
    const arm = {
      pivot: [s * 0.95, 2.3, 0.05] as [number, number, number], swing: ARM_SWING, sphase: armPhase(s),
      raise: strike ? -2.35 : -0.3,
    };
    b.add(limb([s * 0.95, 2.3, 0.05], [s * 1.12, 1.6, 0.18], 0.23, 0.2, 10), { color: PAL.iron, ...arm });
    b.add(blob(0.22, s * 1.13, 1.6, 0.17, [1, 1, 1], 8, 5), { color: STONE_DARK, ...arm });
    b.add(limb([s * 1.13, 1.6, 0.18], [s * 1.17, 0.84, 0.32], 0.28, 0.38, 10), { color: STONE, ...arm });
    b.add(at(plate(0.18, 0.62, 0.5, 0.05, 1), s * 1.43, 1.2, 0.25, -0.15, 0, s * 0.06), { color: STONE_DARK, ...arm });
    b.add(at(plate(0.04, 0.42, 0.04, 0.005, 1), s * 1.2, 1.2, 0.6, -0.17, 0, s * 0.2), { color: crack, glow: true, ...arm });
    if (!elite) {
      b.add(at(plate(0.56, 0.5, 0.56, 0.1), s * 1.17, 0.62, 0.34), { color: STONE_DARK, ...arm });
      b.add(at(plate(0.6, 0.14, 0.2, 0.05, 1), s * 1.17, 0.66, 0.6), { color: PAL.iron, ...arm });
    } else {
      // Furnace Executioner: armoured striking fists — iron gauntlet, copper cuff, knuckle spikes, glowing seam
      b.add(at(plate(0.72, 0.62, 0.68, 0.1), s * 1.18, 0.6, 0.36), { color: PAL.iron, ...arm });
      b.add(drum(0.4, 0.4, 0.16, s * 1.17, 0.96, 0.31, 0, 0, 0, 10), { color: PAL.copper, ...arm });
      for (const k of [-1, 0, 1]) b.add(spike(0.08, 0.26, s * 1.18 + k * 0.22, 0.62, 0.78, Math.PI / 2), { color: PAL.ironLight, ...arm });
      b.add(at(plate(0.62, 0.04, 0.04, 0.005, 1), s * 1.18, 0.82, 0.71), { color: crack, glow: true, ...arm });
    }
  }

  if (elite) {
    // luminous furnace seams on the chest and back, and a ridge of stone horns along the yoke
    b.add(at(plate(0.6, 0.05, 0.05, 0.005, 1), 0, 2.05, 0.57, -0.3), { color: crack, glow: true });
    b.add(at(plate(0.05, 0.4, 0.05, 0.005, 1), -0.32, 1.86, 0.54, -0.2, 0, 0.3), { color: crack, glow: true });
    b.add(at(plate(0.8, 0.05, 0.05, 0.005, 1), 0, 2.3, -0.66, 0.4), { color: crack, glow: true });
    for (const s of [-1, 1]) {
      b.add(spike(0.12, 0.42, s * 0.6, 2.82, -0.1, -0.25, 0, s * -0.35), { color: STONE_LIGHT });
      b.add(spike(0.1, 0.34, s * 1.15, 2.62, -0.05, -0.2, 0, s * -0.8), { color: STONE_LIGHT });
    }
    b.add(at(plate(0.62, 0.1, 0.3, 0.04, 1), 0, 2.47, 0.5, 0.25), { color: PAL.copperDark });
  }
  return b.build();
}

export const ART: EnemyArt = { name: 'Kiln Mauler', build, stride: 2.0, bob: 0.04, outline: 0.04 };
